import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import { isMoEmbedded, moEmbedParentUrl } from "./embed";
import { sanitizeCreditCard } from "../../../sanitizers";

export default class extends Controller {
  static targets = [
    "input",
    "submit",
    "stop",
    "form",
    "pagePath",
  ];
  static values = { chatId: String, stopUrl: String, autoSubmit: Boolean };
  static outlets = ["admin--massimo--attachments"];

  #generating = false;
  #stopping = false;

  connect() {
    this.#generating = !!document.getElementById("massimo-thinking");
    this.#syncButtons();
  }

  get stopping() {
    return this.#stopping;
  }

  enterGenerating() {
    if (this.#generating) return;
    this.#generating = true;
    this.#syncButtons();
    this.updateSubmitState();
  }

  exitGenerating() {
    if (!this.#generating) return;
    this.#generating = false;
    // Note: #stopping is intentionally NOT cleared here. Catch-up reconnects do
    // a synchronous remove+append of the spinner; clearing on every end event
    // would defeat the outlet replay path. It's reset on the next turn's submit.
    this.#syncButtons();
    // Only restore focus if it was lost (e.g. the stop button was just hidden).
    // Don't yank focus from the input the user is typing in, or any other element.
    const active = document.activeElement;
    if (this.hasInputTarget && (!active || active === document.body)) {
      this.#focusInputAtEnd(this.inputTarget);
    }
    this.updateSubmitState();
  }

  inputTargetConnected(element) {
    if (!this.#consumePendingPrompt()) this.#restoreDraft();
    this.autogrow();
    this.#focusInputAtEnd(element);
    this.updateSubmitState();
    this.#autoSubmit();
  }

  adminMassimoAttachmentsOutletConnected(outlet) {
    if (this.#chatId === "new") {
      const pending = sessionStorage.getItem("massimo-files");
      sessionStorage.removeItem("massimo-files");
      if (pending) outlet.addUploaded(JSON.parse(pending));
    }
    this.updateSubmitState();
    this.#autoSubmit();
  }

  submitTargetConnected() {
    this.#syncButtons();
  }

  stopTargetConnected() {
    this.#syncButtons();
  }

  formTargetConnected(element) {
    // Stamp the page the user is on at send time so Mo can pick up page context for the turn.
    // Runs on the form's native submit event (before Turbo serializes the form), so it covers
    // every send path: Enter, the submit button, auto-submit, and the undo shortcut.
    element.addEventListener("submit", () => {
      if (this.hasPagePathTarget) this.pagePathTarget.value = this.#pagePath();
    });
    element.addEventListener("turbo:submit-end", (event) => {
      if (event.detail.success) {
        if (this.hasInputTarget) {
          this.inputTarget.value = "";
          this.inputTarget.style.height = "";
          this.autogrow();
        }
        this.#clearDraft();
        this.#stopping = false;
        if (this.hasStopUrlValue) this.enterGenerating();
        this.updateSubmitState();
      }
    });
  }

  // Sends a canned prompt (a checklist step) through the normal submit path and generating guard.
  submitPrompt(event) {
    const button = event.currentTarget;
    const prompt = button.dataset.prompt;
    if (!prompt || this.#generating || !this.hasFormTarget || !this.hasInputTarget) return;
    const draft = this.inputTarget.value.trim();
    if (draft && draft !== prompt && !window.confirm("Send this checklist step instead of your typed draft?")) {
      return;
    }
    // Only once the send is certain: a chip queued behind a refused send stays in the composer and
    // would ride along with the user's next, unrelated message.
    this.application
      .getControllerForElementAndIdentifier(button, "admin--add-to-mo")
      ?.add();
    this.inputTarget.value = prompt;
    this.updateSubmitState();
    this.formTarget.requestSubmit();
  }

  updateSubmitState() {
    if (this.#generating) {
      this.submitTarget.disabled = true;
    } else {
      this.submitTarget.disabled = !this.hasContentOrFile();
    }
    this.#saveDraft();
  }

  // Mobile only: desktop keeps its fixed-height box. Grow the textarea with its content; the
  // max-h-[40dvh] + overflow-y-auto utilities cap and scroll it past the limit.
  autogrow() {
    if (!this.hasInputTarget) return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    const el = this.inputTarget;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  submit(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!this.#generating && this.hasContentOrFile()) this.formTarget.requestSubmit();
    }
  }

  hasContentOrFile() {
    return this.inputTarget.value.trim().length > 0 || this.hasSelectedFile();
  }

  hasSelectedFile() {
    return this.hasAdminMassimoAttachmentsOutlet && this.adminMassimoAttachmentsOutlet.count > 0;
  }

  sendUndo() {
    if (this.#generating) return;

    this.inputTarget.value = "Please undo everything you just did.";
    this.updateSubmitState();
    this.formTarget.requestSubmit();
  }

  async sendHandoff(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const url = button.dataset.url;
    if (!url || button.disabled) return;

    button.disabled = true;
    button.dataset.loading = "true";

    try {
      const response = await post(url, { responseKind: "turbo-stream" });
      if (response.ok) {
        // Reload so the re-rendered panel consumes the session flag the handoff set and opens the
        // continuation chat, keeping the user on their current page.
        window.location.reload();
        return;
      }
      console.error(`Massimo handoff request returned ${response.statusCode}`);
    } catch (error) {
      console.error("Massimo handoff request failed:", error);
    }
    this.#flashHandoffError(button);
  }

  #flashHandoffError(button) {
    button.dataset.error = "true";
    delete button.dataset.loading;
    setTimeout(() => {
      button.disabled = false;
      delete button.dataset.error;
    }, 3000);
  }

  async stopGeneration() {
    if (!this.hasStopUrlValue) return;

    if (this.hasStopTarget) this.stopTarget.disabled = true;

    try {
      const response = await post(this.stopUrlValue);
      if (!response.ok) {
        this.#abortStopAttempt(`stop endpoint returned ${response.statusCode}`);
        return;
      }
    } catch (error) {
      this.#abortStopAttempt(error);
      return;
    }

    this.#stopping = true;
    this.dispatch("stopping", { prefix: "massimo", target: window });
  }

  #abortStopAttempt(reason) {
    console.error("Massimo stop request failed:", reason);
    if (this.hasStopTarget) this.stopTarget.disabled = false;
  }

  get #chatId() {
    return this.hasChatIdValue ? this.chatIdValue : sessionStorage.getItem("massimo-chat-id");
  }

  #syncButtons() {
    // Input stays enabled while generating so the user can compose their next
    // message; sending is still gated (submit hidden, Enter ignored) until Mo finishes.
    if (this.hasSubmitTarget) this.submitTarget.hidden = this.#generating;
    if (this.hasStopTarget) {
      this.stopTarget.hidden = !this.#generating;
      this.stopTarget.disabled = false;
    }
  }

  // In the embed the iframe is a third-party context, where a cookie written from JS may be dropped,
  // so an existing chat's draft would silently not persist. sessionStorage is per-tab, which suits the
  // drawer, and is already what the embed uses for the chat id and scroll position.
  #draftInSessionStorage(chatId) {
    return chatId === "new" || isMoEmbedded();
  }

  #draftKey(chatId) {
    return `massimo-draft-${chatId}`;
  }

  #saveDraft() {
    const chatId = this.#chatId;
    if (!chatId) return;
    const typed = this.inputTarget.value;
    // A draft holding a card number is dropped; focusout masks the field and its change event saves that.
    const value = sanitizeCreditCard(typed) === typed ? typed : "";

    if (this.#draftInSessionStorage(chatId)) {
      const key = this.#draftKey(chatId);
      if (value) {
        sessionStorage.setItem(key, value);
      } else {
        sessionStorage.removeItem(key);
      }
    } else {
      const name = `massimo_draft_${chatId}`;
      if (value) {
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${7 * 24 * 60 * 60}`;
      } else {
        document.cookie = `${name}=; path=/; max-age=0`;
      }
    }
  }

  // Waits for Home's handed-off files, which the attachments outlet takes, so they ride the same send.
  #autoSubmit() {
    if (!this.autoSubmitValue || this.#generating || !this.hasFormTarget || !this.hasInputTarget) return;
    if (sessionStorage.getItem("massimo-files") || !this.hasContentOrFile()) return;
    this.autoSubmitValue = false;
    // Runs while this controller connects, before the form's credit-card-sanitizer can see the submit.
    this.inputTarget.value = sanitizeCreditCard(this.inputTarget.value);
    this.formTarget.requestSubmit();
  }

  // A hand-off beats a stale draft, even Home's empty prompt for a files-only send.
  #consumePendingPrompt() {
    if (this.#chatId !== "new") return false;
    const prompt = sessionStorage.getItem("massimo-prompt");
    if (prompt === null) return false;
    sessionStorage.removeItem("massimo-prompt");
    this.inputTarget.value = prompt;
    return true;
  }

  #restoreDraft() {
    if (this.inputTarget.value) return;
    const chatId = this.#chatId;
    if (!chatId) return;

    let saved;
    if (this.#draftInSessionStorage(chatId)) {
      saved = sessionStorage.getItem(this.#draftKey(chatId));
    } else {
      const match = document.cookie.match(new RegExp(`massimo_draft_${chatId}=([^;]*)`));
      if (match) saved = decodeURIComponent(match[1]);
    }
    if (saved) this.inputTarget.value = saved;
  }

  #clearDraft() {
    const chatId = this.#chatId;
    if (!chatId) return;

    if (this.#draftInSessionStorage(chatId)) {
      sessionStorage.removeItem(this.#draftKey(chatId));
    } else {
      document.cookie = `massimo_draft_${chatId}=; path=/; max-age=0`;
    }
  }

  // The page to stamp for Mo's page context at send time. In the embed the iframe's own pathname is
  // always /admin/massimo/embed, so Salespath's parent page URL (data-mo-parent-url) is used instead,
  // falling back to the pathname when Salespath omitted it.
  #pagePath() {
    if (isMoEmbedded()) return moEmbedParentUrl() || window.location.pathname;
    return window.location.pathname;
  }

  #focusInputAtEnd(element) {
    element.focus();
    const position = element.value.length;
    element.setSelectionRange(position, position);
  }
}
